import { useState, useMemo } from "react";
import { Link2, Check, ExternalLink } from "lucide-react";

interface Integration {
  name: string;
  description: string;
  connected: boolean;
  icon: string;
  category: "Workspace" | "Citation" | "Publishing";
  url: string;
}

type IntegrationCategory = Integration["category"];

const CATEGORY_META: Record<IntegrationCategory, { label: string; accent: string }> = {
  Workspace: { label: "Workspace & Productivity", accent: "text-amber-200" },
  Citation: { label: "Reference & Drafting", accent: "text-emerald-200" },
  Publishing: { label: "Publishing & Review", accent: "text-sky-200" },
};

const CATEGORY_ORDER: IntegrationCategory[] = ["Workspace", "Citation", "Publishing"];

const INITIAL_INTEGRATIONS: Integration[] = [
  { name: "Notion", description: "Send research blocks into your workspace", connected: false, icon: "🧠", category: "Workspace", url: "https://www.notion.so" },
  { name: "Google Docs", description: "Sync drafts with Docs", connected: false, icon: "📝", category: "Workspace", url: "https://docs.google.com" },
  { name: "Google Sheets", description: "Embed data tables and trackers", connected: false, icon: "📊", category: "Workspace", url: "https://sheets.google.com" },
  { name: "Google Slides", description: "Export outlines to decks", connected: false, icon: "🎞️", category: "Workspace", url: "https://slides.google.com" },
  { name: "Grammarly", description: "Advanced grammar and clarity edits", connected: false, icon: "✓", category: "Citation", url: "https://www.grammarly.com" },
  { name: "Zotero", description: "Collect and cite scholarly sources", connected: false, icon: "🔖", category: "Citation", url: "https://www.zotero.org" },
  { name: "Mendeley", description: "Reference manager with PDF markup", connected: false, icon: "📖", category: "Citation", url: "https://www.mendeley.com" },
  { name: "EndNote", description: "Enterprise citation workflows", connected: false, icon: "📚", category: "Citation", url: "https://endnote.com" },
  { name: "Petal", description: "Modern collaborative citation workspace", connected: false, icon: "🌸", category: "Citation", url: "https://petal.dev" },
  { name: "Turnitin", description: "Plagiarism and originality review", connected: false, icon: "🔍", category: "Publishing", url: "https://www.turnitin.com" },
  { name: "Elsevier Author Hub", description: "Submit directly to Elsevier journals", connected: false, icon: "🌐", category: "Publishing", url: "https://www.elsevier.com/authors" },
  { name: "SpringerLink", description: "Transfer drafts to Springer templates", connected: false, icon: "📘", category: "Publishing", url: "https://link.springer.com" },
];

export default function IntegrationsPanel() {
  const [integrations, setIntegrations] = useState<Integration[]>(INITIAL_INTEGRATIONS);

  const grouped = useMemo(() => {
    return integrations.reduce<Record<string, Integration[]>>((acc, integration) => {
      acc[integration.category] = acc[integration.category] || [];
      acc[integration.category].push(integration);
      return acc;
    }, {});
  }, [integrations]);

  const toggleIntegration = (name: string) => {
    setIntegrations((prev) =>
      prev.map((integration) =>
        integration.name === name
          ? { ...integration, connected: !integration.connected }
          : integration
      )
    );
  };

  return (
    <div className="h-full flex flex-col overflow-hidden">
      <div className="flex items-center gap-2 mb-4">
        <Link2 className="w-5 h-5 text-white/90" />
        <h3 className="text-lg font-semibold text-white">Integrations</h3>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto space-y-5 pr-1">
        {CATEGORY_ORDER.map((category) => {
          const list = grouped[category];
          if (!list || list.length === 0) return null;
          return (
            <section key={category}>
            <div className="flex items-center gap-2 mb-2">
              <span className={`text-xs uppercase tracking-[0.35em] ${CATEGORY_META[category]?.accent || "text-white/70"}`}>
                {CATEGORY_META[category]?.label || category}
              </span>
            </div>
            <div className="space-y-3">
              {list.map((integration) => (
                <div
                  key={integration.name}
                  className="bg-white/10 backdrop-blur-md border border-white/20 rounded-xl p-4 shadow-lg hover:bg-white/15 transition-all duration-200"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-3 flex-1">
                      <span className="text-2xl">{integration.icon}</span>
                      <div className="flex-1">
                        <h4 className="text-sm font-medium text-white mb-1">{integration.name}</h4>
                        <p className="text-xs text-white/70">{integration.description}</p>
                      </div>
                    </div>
                    <button
                      onClick={() => toggleIntegration(integration.name)}
                      className={`px-4 py-1.5 rounded-lg text-xs font-medium transition-all duration-200 border ${
                        integration.connected
                          ? "bg-green-500/20 border-green-400/40 text-green-300"
                          : "bg-white/10 border-white/20 text-white hover:bg-white/20"
                      }`}
                    >
                      {integration.connected ? (
                        <span className="flex items-center gap-1">
                          <Check className="w-3 h-3" />
                          Connected
                        </span>
                      ) : (
                        "Connect"
                      )}
                    </button>
                  </div>

                  <div className="mt-3 pt-3 border-t border-white/10 flex items-center justify-between text-xs">
                    <span className="text-white/60">{integration.category}</span>
                    <button
                      type="button"
                      onClick={() => window.open(integration.url, "_blank", "noopener,noreferrer")}
                      className="flex items-center gap-2 text-white/70 hover:text-white transition-all duration-200"
                    >
                      <ExternalLink className="w-3 h-3" />
                      Open {integration.name}
                    </button>
                  </div>
                </div>
              ))}
            </div>
            </section>
          );
        })}
      </div>

      <div className="mt-4 bg-white/10 backdrop-blur-md border border-white/20 rounded-xl p-4 shadow-lg">
        <p className="text-xs text-white/70 text-center">
          More integrations coming soon! Connect your favorite writing tools to enhance your workflow.
        </p>
      </div>
    </div>
  );
}
